import { ChevronDownIcon, SearchIcon, SendIcon } from "lucide-react";

import {
  Center,
  Column,
  ErrorText,
  LoadingIndicator,
  Row,
  Text,
} from "@/components";
import { ReadOnlyField } from "@/components/companies/shared/ReadOnlyField";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

import { useGetSourcingRequest } from "@/hooks/queries/useGetSourcingRequest";
import {
  candidateLastActiveOptions,
  EXTERNAL_SOURCING_NO_TICKET_MESSAGE,
  excludeContactedOptions,
  resumeLastUpdatedOptions,
  sourcingRadiusOptions,
  timeAtCurrentJobOptions,
} from "@/lib/constants";
import { SourcingRequest } from "@/types";

// Map a stored option code (e.g. "6mo") to its human label, falling back to
// the raw value if it's not a known option.
const optionLabel = (
  options: { label: string; value: string }[],
  value: string | null | undefined,
) =>
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  options.find((option) => option.value === value)?.label || value || null;

const ConfigCard = ({
  children,
  icon,
  title,
}: {
  children: React.ReactNode;
  icon: React.ReactNode;
  title: string;
}) => (
  <Column className="gap-4 rounded-lg border border-slate-200 p-4">
    <Row className="items-center gap-2 border-b border-slate-200 pb-4">
      {icon}
      <Text className="text-base font-semibold text-slate-800">{title}</Text>
    </Row>
    {children}
  </Column>
);

const ConfigDetails = ({
  sourcingRequest,
}: {
  sourcingRequest: SourcingRequest;
}) => {
  const { searchTerms } = sourcingRequest;

  return (
    <Column className="gap-4">
      <ConfigCard
        icon={<SearchIcon className="h-4 w-4 text-primary-700" />}
        title="Search parameters"
      >
        <ReadOnlyField
          mono
          label="Search query"
          value={searchTerms.booleanSearch}
        />
        <Row className="gap-4">
          <ReadOnlyField
            className="flex-1"
            label="Search radius"
            value={optionLabel(
              sourcingRadiusOptions,
              String(sourcingRequest.radiusMiles),
            )}
          />
          <ReadOnlyField
            className="flex-1"
            label="Exclude contacted candidates"
            value={optionLabel(
              excludeContactedOptions,
              searchTerms.excludeContactedCandidates,
            )}
          />
        </Row>
        <Row className="gap-4">
          <ReadOnlyField
            className="flex-1"
            label="Resume last updated"
            value={optionLabel(
              resumeLastUpdatedOptions,
              searchTerms.resumeLastUpdated,
            )}
          />
          <ReadOnlyField
            className="flex-1"
            label="Candidate last active"
            value={optionLabel(
              candidateLastActiveOptions,
              searchTerms.candidateLastActive,
            )}
          />
          <ReadOnlyField
            className="flex-1"
            label="Time at current job"
            value={optionLabel(
              timeAtCurrentJobOptions,
              searchTerms.timeAtCurrentJob,
            )}
          />
        </Row>
      </ConfigCard>

      <ConfigCard
        icon={<SendIcon className="h-4 w-4 text-primary-700" />}
        title="Outreach parameters"
      >
        <Row className="gap-4">
          <ReadOnlyField
            className="flex-1"
            label="Max messages"
            value={sourcingRequest.maxMessages?.toString()}
          />
          <ReadOnlyField
            className="flex-1"
            label="Minimum score"
            value={
              sourcingRequest.minimumScore
                ? `${sourcingRequest.minimumScore.toString()} stars and up`
                : null
            }
          />
        </Row>
      </ConfigCard>
    </Column>
  );
};

export const SourcingConfigurationSection = () => {
  const { data: sourcingRequest, isPending, isError } = useGetSourcingRequest();

  /* Render */
  const renderContent = () => {
    if (isPending) {
      return (
        <Center className="py-6">
          <LoadingIndicator variant="small" />
        </Center>
      );
    }
    if (isError) {
      return (
        <ErrorText errorMessage="Failed to load sourcing configuration." />
      );
    }
    if (!sourcingRequest) {
      return <Text variant="muted">{EXTERNAL_SOURCING_NO_TICKET_MESSAGE}</Text>;
    }
    return <ConfigDetails sourcingRequest={sourcingRequest} />;
  };

  return (
    <Collapsible className="group/collapsible">
      <Column className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <CollapsibleTrigger className="flex w-full cursor-pointer items-center justify-between px-6 py-4">
          <Text variant="header">External Sourcing Configuration</Text>
          <ChevronDownIcon className="h-4 w-4 text-slate-500 transition-transform duration-200 group-data-[state=closed]/collapsible:-rotate-90" />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Column className="border-t border-slate-100 px-6 pb-5 pt-4">
            {renderContent()}
          </Column>
        </CollapsibleContent>
      </Column>
    </Collapsible>
  );
};
